import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { validateProps } from '../../../../../utils/propValidator';
import { FormFieldWrapper } from '../../layout';

const propRules = {
  id: { type: 'string' },
  name: { type: 'string' },
  label: { type: 'string' },
  value: { type: ['object', 'null'] },
  onChange: { type: 'function', required: true },
  disabled: { type: 'boolean' },
  disablePast: { type: 'boolean' },
  minDate: { type: 'object' },
  views: { type: 'array' },
  format: { type: 'string' },
  error: { type: 'boolean' },
  errorMessage: { type: 'string' },
  showErrorInline: { type: 'boolean' },
  helperText: { type: 'string' },
  orientation: { oneOf: ['row', 'column'] },
  sx: { type: 'object' },
};

/**
 * Merge a date or time value into an existing datetime.
 * Falls back to current datetime when no value exists.
 */
const mergeDateTime = (current, next) => {
  const base = current ?? dayjs();

  return base
    .year(next.year())
    .month(next.month())
    .date(next.date())
    .hour(next.hour())
    .minute(next.minute())
    .second(0);
};

/**
 * DateTimeInput
 *
 * Controlled date + time input that manages a single datetime value.
 * DatePicker and TimePicker edit different parts of the same value.
 *
 * This component:
 * - Does not manage form state
 * - Does not perform validation
 * - Can optionally render errors inline via FormFieldWrapper
 *
 * @param {Object} props
 * @param {string} [props.id] - Input id
 * @param {string} [props.name] - Input name
 * @param {string} [props.label] - Field label
 * @param {dayjs.Dayjs|null} props.value - Current datetime value
 * @param {(value: dayjs.Dayjs|null) => void} props.onChange - Change handler
 * @param {boolean} [props.disabled] - Disable input
 * @param {boolean} [props.error] - Error state
 * @param {string} [props.errorMessage] - Error message
 * @param {boolean} [props.showErrorInline=true] - Whether to render error inline
 * @param {string} [props.helperText] - Helper text (character count style)
 * @param {'row'|'column'} [props.orientation='column'] - Layout orientation
 * @param {Object} [props.wrapperSx] - Wrapper sx styles
 */
export default function DateTimeInput(props: any) {
  validateProps(props as Record<string, unknown>, propRules, 'DateTimeInput');

  const {
    id,
    name,
    label,
    value,
    onChange,
    disabled,
    disablePast = true,
    minDate = dayjs(),
    views = ['day'],
    format = 'DD/MM/YYYY',
    error = false,
    errorMessage,
    showErrorInline = true,
    helperText,
    orientation = 'column',
    sx = {},
  } = props;

  const shouldShowError = showErrorInline && error;

  return (
    <FormFieldWrapper
      label={label}
      htmlFor={id}
      orientation={orientation}
      error={shouldShowError}
      errorMessage={shouldShowError ? errorMessage : undefined}
      helperText={helperText}
      sx={sx}
    >
      <div className='row gap-3'>
        <DatePicker
          value={value}
          onChange={(date) => {
            if (!date) {
              onChange(null);
              return;
            }
            onChange(mergeDateTime(value, date));
          }}
          name={name}
          disablePast={disablePast}
          views={views}
          format={format}
          minDate={minDate}
          disabled={disabled}
          slotProps={{
            inputAdornment: { sx: { mr: 1.5 } },
            popper: { sx: { '& .MuiDateCalendar-root': { height: 'auto' } } },
            openPickerIcon: { sx: { width: 18, height: 18 } },
            day: {
              sx: {
                fontSize: '0.875rem',
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: (theme: any) => `${(theme as any).palette.primary.light} !important`,
                },
              },
            },
            monthButton: {
              sx: {
                fontSize: '0.875rem',
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: (theme: any) => `${(theme as any).palette.primary.light} !important`,
                },
              },
            },
            yearButton: {
              sx: {
                fontSize: '0.875rem',
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: (theme: any) => `${(theme as any).palette.primary.light} !important`,
                },
              },
            },
          }}
        />

        <TimePicker
          value={value}
          onChange={(time) => {
            if (!time) {
              onChange(null);
              return;
            }
            onChange(mergeDateTime(value, time));
          }}
          disabled={disabled}
          slotProps={{
            inputAdornment: { sx: { mr: 1.5 } },
            openPickerIcon: { sx: { width: 18, height: 18 } },
            actionBar: { sx: { display: 'none' } },
            digitalClockSectionItem: {
              sx: {
                fontSize: '0.875rem',
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: (theme) => theme.palette.primary.light,
                  '&:hover': { backgroundColor: (theme) => theme.palette.primary.light },
                },
              },
            },
          }}
        />
      </div>
    </FormFieldWrapper>
  );
}
